/**
 * Imperative toasts for code that cannot call `useToast()` — module scope,
 * async handlers written outside a component, or files that predate the hook.
 *
 * Same call shape as react-hot-toast (`toast(msg)`, `toast.success(msg)`,
 * `toast.error(msg)`), but everything lands in ToastProvider's stack. Several
 * screens imported react-hot-toast directly while no `<Toaster>` was ever
 * mounted, so their messages — including form validation — never rendered.
 */

type ToastType = 'success' | 'error' | 'warning' | 'info';

type Sink = (type: ToastType, message: string) => void;

let sink: Sink | null = null;

/** Called by ToastProvider on mount; returns the unregister function. */
export function registerToastSink(next: Sink): () => void {
  sink = next;
  return () => {
    if (sink === next) sink = null;
  };
}

function emit(type: ToastType, message: string) {
  if (sink) sink(type, message);
  else if (import.meta.env.DEV) console.warn(`[toast:${type}]`, message);
}

export const toast = Object.assign((message: string) => emit('info', message), {
  success: (message: string) => emit('success', message),
  error: (message: string) => emit('error', message),
  warning: (message: string) => emit('warning', message),
  info: (message: string) => emit('info', message),
});
